import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createTask, listTasks, getGoalTree, listLabels, type Task } from '../api';
import { parseWhen, stripWhen } from '../parseWhen';
import { useAccountDay } from '../accountDay';
import { Icon } from './Icon';
import { Select } from './Select';
import { MultiSelect } from './MultiSelect';
import { goalOptions, renderGoalOption } from './pickerOptions';
import { FailureNote } from './FailureNote';
import { useProjectRole } from '../roles';
import { readableDay } from '../dateLabels';

/** Three levels plus "none", which the picker offers as its empty choice. */
const PRIORITY_OPTIONS = [
  { value: '1', label: 'Low' },
  { value: '2', label: 'Medium' },
  { value: '3', label: 'High' },
];

/**
 * Getting a thought out of your head, from wherever you are.
 *
 * Capture existed on the Tasks screen only. Today is the default screen and had no unconditional
 * way to add anything; the goal detail screen lists a goal's tasks and cannot create one; Habits,
 * Projects, Reflection, Templates and Settings had nothing at all. So the thought had to survive
 * a tab click, which for this persona is the difference between using the tool and not.
 *
 * Two things it does beyond the old box, both from watching a persona use it:
 *
 * - **It reads a date out of the sentence** and offers it as a chip, off until you press it. Eight
 *   of their 21 captures carried one and all 21 landed undated. It suggests and never applies —
 *   see `parseWhen` for why that distinction matters.
 * - **It notices a duplicate.** They captured "buy milk" twice a week apart, both accepted
 *   silently. Said, not blocked: sometimes you do want two.
 */
export function Capture({
  compact = false,
  project = null,
  /**
   * Focus on mount. Only ever set by `CaptureDisclosure`, where the box did not exist a moment
   * ago and the user pressed something to bring it into being — so focus is what they asked for.
   * Phase 61 removed the unconditional `autoFocus`, which stole every keyboard shortcut on the
   * Tasks screen; this is the narrow case that was actually wanted.
   */
  autoFocusInput = false,
  onCreated,
}: {
  compact?: boolean;
  autoFocusInput?: boolean;
  /**
   * Told about the task once it exists.
   *
   * Only used by the calendar's drag-to-create, which needs to hang a time block off the new task.
   * Passing a callback rather than lifting the whole mutation out keeps every capture surface —
   * header, Tasks screen, calendar — creating tasks through one path, so a task drawn on the
   * timeline is the same kind of thing as one typed anywhere else, facets and all.
   */
  onCreated?: (created: Task) => void;
  /**
   * The project the screen around this box is filtered to, when there is one.
   *
   * Capture has never set `projectId` and still does not — every capture is a personal task. From
   * a list filtered to a project that reads as filing into it, and a reporter who typed into this
   * box got a 201 for a task that landed in their own account with `projectId: null`. The one
   * mutation their role allowed went somewhere other than where the screen implied, silently.
   *
   * Naming the destination rather than changing it: a project is not where quick thoughts belong,
   * the Projects screen already has a box that files into one, and creating in a project needs
   * `write` — so making this write there would have swapped a silent misfile for a silent 403 on
   * every role below maintainer.
   */
  project?: { id: string; title: string } | null;
}) {
  const queryClient = useQueryClient();
  const { can } = useProjectRole(project?.id ?? null);
  const [draft, setDraft] = useState('');
  const [goalId, setGoalId] = useState('');
  /*
    The rest of the facet row. Kept between captures, deliberately — see the note by the row.
  */
  const [labelIds, setLabelIds] = useState<string[]>([]);
  const [priority, setPriority] = useState(0);
  /*
    Whether the defaults row is showing. `hasFacets` keeps it open whenever something is actually
    set, so a sticky default is never hidden from the person it will apply to.
  */
  const [facetsOpen, setFacetsOpen] = useState(false);
  const hasFacets = goalId !== '' || labelIds.length > 0 || priority > 0;
  const allLabels = useQuery({ queryKey: ['labels'], queryFn: listLabels });
  const labelOptions = (allLabels.data ?? []).map((l) => ({ value: l.id, label: l.name }));
  /*
    Off by default, which is what `parseWhen` always said it was.

    Its rule one reads "It suggests, never applies — a parser that silently writes dates would be
    wrong often enough to have to be distrusted", with the worked example "chase the invoice from
    June", where June is not when it is due. This flag started `true`, so the date *was* applied
    unless you noticed the chip and switched it off. The code did the thing its own comment argues
    against; now it does not.
  */
  const [useDate, setUseDate] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  const goals = useQuery({ queryKey: ['goals', 'tree'], queryFn: getGoalTree });
  const options = goalOptions(goals.data ?? []);

  /*
    Anchored on the account's day, not the browser's — see `parseWhen`. Noon, because `parseWhen`
    does its whole-day arithmetic with `Date.setDate` and noon keeps it away from both midnights.
  */
  const { today } = useAccountDay();
  const parsed = parseWhen(draft, new Date(`${today}T12:00:00`));
  const title = parsed && useDate ? stripWhen(draft, parsed.matched) : draft.trim();

  /*
    Does this already exist?

    Only asked once the draft is worth asking about, and only against open work — capturing
    something you finished last year again is not a duplicate worth mentioning.
  */
  const existing = useQuery({
    queryKey: ['tasks', { duplicate: title }],
    queryFn: () => listTasks({ q: title }),
    enabled: title.trim().length >= 4,
  });
  const duplicate = (existing.data ?? []).find(
    (t) => t.title.toLowerCase() === title.trim().toLowerCase() && t.status !== 'done',
  );

  const add = useMutation({
    mutationFn: async (submitted: { title: string; raw: string }) => {
      /*
        One request, labels included.

        This was two: a create, then `setTaskLabels`. The comment here used to say the second call
        existed because `labelIds` on a create would need a second implementation of the
        one-answer-per-group rule — which was never true, since `createTask` applies the field
        through `replaceLabels`, which *is* that rule. What was actually true is that
        `POST /api/tasks` discarded the field and answered 201 anyway, so the follow-up was the
        only request that landed. `01a03cdb-a0ad` fixed the door; confirmed before deleting the
        workaround rather than trusting the note — a create carrying `labelIds` answers 201 with
        the label on the body.

        Two writes where one will do is a capture that can half-land: the task exists unlabelled if
        the second request fails, and what the user sees is a task appearing without its filing.
      */
      return createTask({
        title: submitted.title,
        ...(goalId ? { goalIds: [goalId] } : {}),
        ...(priority > 0 ? { priority } : {}),
        ...(parsed && useDate ? { scheduledDate: parsed.date } : {}),
        ...(labelIds.length > 0 ? { labelIds } : {}),
      });
    },
    /*
      Clear only if the field still holds what was submitted (I-78).

      The submitted text is passed as the mutation variable rather than read from the closure.
      Closing over `draft` looks equivalent and is not: `onSuccess` is redefined on every render,
      so by the time it runs it can be holding the *next* thing you typed and will clear that
      instead. Capture two things quickly and the second vanishes as you type it — which an
      existing spec caught within a minute of me making exactly that mistake.
    */
    onSuccess: (created, submitted) => {
      setDraft((current) => (current === submitted.raw ? '' : current));
      /*
        Back to the default, which is off. A press is consent for one sentence, not for the next.

        This line said `true` from `5d79098`, when on *was* the default and resetting to it was
        right; `d1c15a0` flipped the default above and left this reset behind, so every capture
        after the first had its date word stripped and applied unasked (`01a15a31-1b18`). Not
        deleted: without a reset, a chip pressed for one capture would stay pressed for the next.
      */
      setUseDate(false);
      void queryClient.invalidateQueries();
      onCreated?.(created);
    },
  });

  useEffect(() => {
    if (autoFocusInput) input.current?.focus();
  }, [autoFocusInput]);

  // `c` focuses this from anywhere, the same treatment `/` already gives search — and with the
  // same guard, so it does not fire while you are typing into something else.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'c' || e.metaKey || e.ctrlKey || e.altKey) return;
      const el = document.activeElement;
      const typing =
        el instanceof HTMLInputElement ||
        el instanceof HTMLTextAreaElement ||
        el instanceof HTMLSelectElement ||
        (el instanceof HTMLElement && el.isContentEditable);
      if (typing) return;
      e.preventDefault();
      input.current?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className={`capture-box ${compact ? 'compact' : ''}`}>
      <form
        className="capture"
        onSubmit={(e) => {
          e.preventDefault();
          if (title.trim()) add.mutate({ title: title.trim(), raw: draft });
        }}
      >
        {/*
          No autofocus.

          The full version used to grab focus the moment the Tasks screen mounted, which made
          every keyboard shortcut on that screen unreachable: `j`, `k`, `x` and the number keys all
          went into this box, and the typing guard above correctly ignored them. It also meant the
          arrow keys scrolled a text field instead of the page.

          Stealing focus on arrival was only ever a shortcut for "no other way in", and `c` — which
          focuses this from any screen, including this one — is that way in.
        */}
        <input
          ref={input}
          value={draft}
          placeholder="Capture a task"
          aria-label="Capture a task"
          onChange={(e) => setDraft(e.target.value)}
        />
        {/*
          A `+`, not the word.

          Enter already submitted — this is `type="submit"` on a single-input form — so the button
          was never the mechanism, only the visible affordance. It stays as an icon rather than
          disappearing because on a phone there is a return key but no contract that it commits,
          and a form whose only submit is invisible reads fine to whoever wrote it.
        */}
        <button
          type="submit"
          className="capture-submit"
          disabled={!title.trim() || add.isPending}
          aria-label="Capture"
          title="Capture — Enter"
        >
          <Icon name="plus" size={16} />
        </button>
        {/*
          The shortcut, said out loud.

          It used to live inside the placeholder as `"Capture  c"`, copying `"Search  /"`. The
          owner asked what "capture c" meant, which is the answer: two words jammed together with
          no separator read as a label, not as a key. A `kbd` beside the field says what it is,
          and survives the placeholder disappearing the moment you type.
        */}
        {/*
          Shown in the header too — which is the only place the key is worth advertising, since
          that is the capture box `c` focuses from every screen. Gating this on `!compact` meant
          the hint rendered only on the one screen you did not need it. Same mistake as the goal
          dropdown, two commits earlier.
        */}
        <span className="key-hint" aria-hidden="true">
          <kbd>c</kbd>
        </span>
      </form>

      {/*
        The second row: what this task is, set once and kept.

        The values stick between captures, which is the whole argument for the row existing. The
        goal select already behaved this way — there is a spec called "the goal stays selected
        between captures" — and extending it to the rest means setting `work · P2` once tags
        everything you capture in that sitting. The row then costs one interaction per session
        rather than per task, which turns it from a tax into the most direct fix available for one
        task in twenty-one carrying any classification.

        Sticky per instance and in memory only: a label set while filing on the Tasks screen should
        not silently attach itself to a thought captured from Habits three hours later, and one
        that survived a browser restart would eventually tag something wrong unnoticed.

        No colour and no status. Colour at capture permanently overrides the inheritance chain for
        a decision made before you know anything about the task; status is almost always `inbox`,
        and `inbox` has been fully actionable since Phase 61.
      */}
      {/*
        Behind a disclosure, because of where it sat rather than what it is.

        These are capture *defaults* — what the next thing you type will carry — but they were
        stacked directly above the search field and the filter row, in the band the eye reads as
        filters. So the obvious move was to pick a goal here and wonder why the list did not
        change. The popover form already collapses these the same way.

        The row stays visible whenever a facet is *set*, never collapsing a sticky default out of
        sight: the note above warns that a default which outlives your attention eventually tags
        something wrong unnoticed, and hiding it would be the fastest way to arrange that. So this
        only ever hides an empty row, and the toggle names what is set when it is closed.
      */}
      {/*
        A door that opens both ways.

        This only ever called `setFacetsOpen(true)`, and the toggle then vanished from the DOM —
        so the row could not be folded back in the session, and a reload was the only way out.
        Two personas reported it; the UX reviewer added the sharper point, that opened it puts
        three placeholder dropdowns directly above the search and filter row, which is the exact
        confusion the note below says this disclosure exists to prevent.

        The rule it protects is untouched: a facet that is *set* still holds the row open and
        removes the toggle, because a sticky default nobody can see eventually tags something
        wrong. Only an **empty** row can be closed again, which was always the intent.
      */}
      {!hasFacets && (
        <button
          type="button"
          className="link capture-more"
          aria-expanded={facetsOpen}
          onClick={() => setFacetsOpen((v) => !v)}
        >
          {facetsOpen ? '− details' : '+ details'}
        </button>
      )}
      {(facetsOpen || hasFacets) && (
        <div className="capture-facets">
          {options.length > 0 && (
            <Select
              compact
              options={options}
              value={goalId || null}
              onChange={(next) => setGoalId(next ?? '')}
              placeholder="Goal"
              emptyLabel="No goal"
              ariaLabel="Attach to goal"
              renderOption={renderGoalOption}
            />
          )}
          {labelOptions.length > 0 && (
            <MultiSelect
              compact
              options={labelOptions}
              value={labelIds}
              onChange={setLabelIds}
              placeholder="Labels"
              ariaLabel="Labels"
              dialogTitle="Labels for what you capture"
            />
          )}
          <Select
            compact
            options={PRIORITY_OPTIONS}
            value={priority === 0 ? null : String(priority)}
            onChange={(next) => setPriority(next ? Number(next) : 0)}
            placeholder="Priority"
            emptyLabel="No priority"
            ariaLabel="Priority"
          />
        </div>
      )}

      {parsed && (
        <p className="subtext capture-chip" data-testid="capture-when">
          <button
            type="button"
            className={useDate ? 'pill ok' : 'pill'}
            aria-pressed={useDate}
            onClick={() => setUseDate((v) => !v)}
            title={useDate ? 'Do not use this date' : 'Use this date'}
          >
            {useDate ? '✓ ' : ''}
            {readableDay(parsed.date, 'weekday')}
          </button>{' '}
          from “{parsed.matched}”
        </p>
      )}

      {duplicate && (
        <p className="subtext" data-testid="capture-duplicate">
          You already have “{duplicate.title}” open. Adding it again is fine if you meant to.
        </p>
      )}

      {/* Where it lands, said before you press Enter rather than discovered afterwards. */}
      {project && (
        <p className="subtext" data-testid="capture-lands">
          Lands in your own tasks, not in {project.title} —{' '}
          {can('write')
            ? 'file it there from the Projects screen.'
            : 'your role there does not let you add tasks.'}
        </p>
      )}

      <FailureNote error={add.error} testId="capture-error" />
    </div>
  );
}
